import React, { useState } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { graphql } from 'babel-plugin-relay/macro';
import { useFragment } from 'react-relay';

import { Container, Row, Col } from 'react-bootstrap';
import Slider from 'react-slick';
import Person from '../../uikit/Cards/Person';
import Tabpanel from '../../components/Tabpanel';
import AgendaItem from '../../components/AgendaItem';
import AgendaItemEnd from '../../components/AgendaItemEnd';
import './Agenda.sass';

import type { AgendaPageQuery$key, AgendaPageQuery } from './__generated__/AgendaPageQuery.graphql';

interface Props {
  query: AgendaPageQuery$key;
}

function Agenda(props: Props) {
  const { tab } = useParams<{ tab?: string }>();

  const data: AgendaPageQuery = useFragment(
    graphql`
			fragment AgendaPageQuery on PublishedContentByTypeQuery {
				agenda {
					items {
						programCommitee {
							... on SpeakerPublishedContent {
								_id
								_url
								speakerName
								speakerImage
								speakerPreviewDescription
								facebookLink
								linkedinLink
								twitterLink
								location {
									... on LocationPublishedContent {
										country {
											... on FilePublishedMedia {
												_url
											}
										}
									}
								}
							}
						}
						_children {
							items {
								... on EventDayPublishedContent {
									_id
									_url
									title
									date
									endDayTime
									endDayText
									_children {
										items {
										... on BreakLinePublishedContent {
                        title
                        text
                        startTime
                        duration
                        isBreakLine
                        language
                        background
                      }
											... on SpeechPublishedContent {
												_id
												duration
												language
												startTime
												text
												title
												track
												type
												speakers {
													... on SpeakerPublishedContent {
														speakerName
														speakerPreviewDescription
														_url
														speakerImage
														facebookLink
														linkedinLink
														twitterLink
														location {
															... on LocationPublishedContent {
																country {
																	... on FilePublishedMedia {
																		_url
																	}
																}
															}
														}
													}
												}
												_parent {
													... on EventDayPublishedContent {
														_id
														title
														date
														endDayTime
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
    props.query
  );

  const settings = {
    dots: false,
    nextArrow: <SampleNextArrow/>,
    prevArrow: <SamplePrevArrow/>,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    swipe: true,

    responsive: [
      {
        breakpoint: 1199,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 800,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const agendaData = data?.agenda?.items?.[0]
  const { programCommitee, _children: children } = agendaData || {};
  const agendaItems = children?.items;

  const initialTabTitle = `${
    agendaItems?.[0]?.title
  } / ${setTabTime(agendaItems?.[0])}`;

  const [tabTitle, setTabTitle] = useState(initialTabTitle);

  if (!tab) {
    const firstTabUrl = agendaItems?.[0]?._url;
    if (firstTabUrl) {
      return <Redirect to={firstTabUrl}/>;
    }
  }
  const currentDayAgendaData = agendaItems?.find((i: any) =>
    i._url.includes(tab)
  );
  const currentAgendaData = currentDayAgendaData?._children?.items || [];

  if (!currentDayAgendaData) {
    return <Redirect to="/404"/>;
  }

  return (
    <div className="agenda-page">
      <Container>
        <Row>
          <Col xs={12}>
            <div className="agenda-page__tabs">
              <Tabpanel
                tabs={agendaData}
                setTabTitle={setTabTitle}
                tabTitle={tabTitle}
              />
            </div>
            <div className="agenda-page__list agenda-list">
              <div className="agenda-list__main">
                {currentAgendaData.map((agenda: any) => (
                  <AgendaItem key={agenda._id} currentAgendaData={agenda}/>
                ))}{' '}
              </div>
              <AgendaItemEnd time={currentDayAgendaData}/>
            </div>
            <div className="agenda-page__tabs agenda-page__tabs--bottom">
              <Tabpanel
                tabs={agendaData}
                setTabTitle={setTabTitle}
                tabTitle={tabTitle}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {programCommitee ? (
              <h2 className="uikit-label-small-caps agenda-page__program-title">
                Program Commitee
              </h2>
            ) : null}
          </Col>
          <Col xs={12}>
            <Slider {...settings} className="home-page-speakers__carousel">
              {programCommitee?.length
                ? programCommitee.map((commitee: any) => (
                  <div
                    className="home-page-speakers__slide"
                    key={commitee._id}
                  >
                    <Person data={commitee}/>
                  </div>
                ))
                : null}
            </Slider>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Agenda;

function SampleNextArrow(props) {
  const { className, onClick } = props;
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-label="Next speaker"
    >
      <svg width="12" height="12" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(-60)">
          <path fill="#000" fillRule="nonzero" d="M60 0h6l6 6-6 6h-6l6-6z"/>
        </g>
      </svg>
    </button>
  );
}

function SamplePrevArrow(props) {
  const { className, onClick } = props;
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-label="Previous speaker"
    >
      <svg width="12" height="12" xmlns="http://www.w3.org/2000/svg">
        <path fill="#000" fillRule="nonzero" d="M12 12H6L0 6l6-6h6L6 6z"/>
      </svg>
    </button>
  );
}

function setTabTime (tab: any) {
  return `${new Date(tab.date).getDate()}.${
    (new Date(tab.date).getMonth() + 1).toString().length === 1
      ? `0${new Date(tab.date).getMonth() + 1}`
      : `${new Date(tab.date).getMonth() + 1}`
  }.${new Date(tab.date).getFullYear()}`;
}
